import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import './UserProfile.css';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../redux/AuthReducer/action';
import ActiveSessionsModal from './ActiveSessionsModal';

/**
 * UserProfileDropdown
 *
 * @param {boolean} sidebarExpanded - when true (sidebar is expanded), show name + email next to avatar
 * @param {function} onToggle - callback when dropdown is toggled open/closed
 */
const UserProfileDropdown = ({ sidebarExpanded, onToggle }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [showSessionsModal, setShowSessionsModal] = useState(false);

    const isPopupDisabled = sidebarExpanded === false;

    // Feature-detect Keycloak session mode from the stored token shape — when the backend
    // flag is off, tokens never carry session_id, so this menu item is simply absent and the
    // dropdown looks exactly as it did before session management existed.
    const hasSessionManagement = (() => {
        try {
            const stored = JSON.parse(sessionStorage.getItem('tokens') || localStorage.getItem('tokens') || 'null');
            return !!stored?.session_id;
        } catch {
            return false;
        }
    })();

    // Automatically close the dropdown if sidebar collapses
    useEffect(() => {
        if (isPopupDisabled && show) {
            setShow(false);
            if (onToggle) onToggle(false);
        }
    }, [isPopupDisabled, show, onToggle]);

    const handleToggle = (nextShow) => {
        if (isPopupDisabled) {
            setShow(false);
            if (onToggle) onToggle(false);
            return;
        }
        setShow(nextShow);
        if (onToggle) onToggle(nextShow);
    };

    let officer = JSON.parse(sessionStorage.getItem("officer")) || {};
    let initials = "";
    if (officer?.first_name) initials += officer.first_name.charAt(0).toUpperCase();
    if (officer?.last_name)  initials += officer.last_name.charAt(0).toUpperCase();
    if (!initials) initials = "U";

    return (
        <Dropdown
            align="end"
            drop="up"
            show={show}
            onToggle={handleToggle}
            className="userProfileContainer"
        >
            <Dropdown.Toggle
                variant="light"
                id="dropdown-user"
                className={`userProfileToggle d-flex align-items-center border-0 bg-transparent ${isPopupDisabled ? 'pe-none' : ''}`}
                style={{ cursor: isPopupDisabled ? 'default' : 'pointer' }}
            >
                <div className="userProfileAvatar">
                    {initials}
                </div>
                {sidebarExpanded && (
                    <div className="userProfileInfo ms-2 text-start">
                        <div className="userProfileName fw-bold">
                            {`${officer?.first_name || 'User'} ${officer?.last_name || ''}`}
                        </div>
                        <div className="userProfileRole text-muted small">
                            {officer?.email || 'Admin'}
                        </div>
                    </div>
                )}
            </Dropdown.Toggle>

            {!isPopupDisabled && (
                <Dropdown.Menu
                    className="shadow-lg border-0 p-3"
                    style={{ minWidth: '260px', borderRadius: '12px', marginBottom: '8px', zIndex: 1100 }}
                >
                    <div className="d-flex align-items-center mb-3 p-2 bg-light rounded">
                        <div className="userProfileAvatar large">
                            {initials}
                        </div>
                        <div className="ms-3">
                            <div className="fw-bold">{`${officer?.first_name || ''} ${officer?.last_name || ''}`}</div>
                            <div className="text-muted small">{officer?.email || ''}</div>
                        </div>
                    </div>
                    <Dropdown.Divider className="my-2" />
                    {hasSessionManagement && (
                        <Dropdown.Item
                            as="button"
                            className="btn w-100 mb-2 text-center fw-semibold"
                            style={{
                                borderRadius: "8px",
                                padding: "10px",
                                color: "#0b2546",
                                backgroundColor: "#fff",
                                border: "1px solid #d1d5db"
                            }}
                            onClick={() => setShowSessionsModal(true)}
                        >
                            Active Sessions
                        </Dropdown.Item>
                    )}
                    <Dropdown.Item
                        as="button"
                        className="btn w-100 mt-2 text-center fw-semibold logoutBtn"
                        style={{
                            borderRadius: "8px",
                            padding: "10px",
                            color: "#dc3545",
                            backgroundColor: "#fff",
                            border: "1px solid #dc3545"
                        }}
                        onClick={() => {
                            let success = dispatch(logout);
                            if (success) navigate("/");
                        }}
                    >
                        Log Out
                    </Dropdown.Item>
                </Dropdown.Menu>
            )}

            {showSessionsModal && (
                <ActiveSessionsModal closeModal={() => setShowSessionsModal(false)} />
            )}
        </Dropdown>
    );
};

export default UserProfileDropdown;